import SvgIcon from '@mui/material/SvgIcon';
import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import * as globalActions from '../../actions/global';
import { fetchingState } from '../../constants';
import { removeNullCharacter } from '../../utils';
import LoadingSpinner from '../_components/loadingSpinner';
import NoDataHolder from '../_components/noDataHolder';
import Pagination from '../_components/pagination';
import RefreshIndicator from '../_components/refreshIndicator';
import RegisterNotificationTooltip from '../_components/registerNotificationTooltip';
import selector from './selector';
import SortIndicator from './sortIndicator';
import './index.scss';

const mainImage = require('../../assets/images/tt-image1-01.png');

const PropertySearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    propertyList,
    queryString,
    office,
    searchResultConfig,
    clientConfig,
    importStatus,
  } = useSelector(selector, shallowEqual);

  const {
    changeCurrentPage,
    changePageSize,
    changeQueryString,
    changeSortModel,
    fetchImportStatus,
    fetchSearchResultConfig,
    search,
  } = useMemo(
    () =>
      bindActionCreators(
        {
          changeCurrentPage: globalActions.changeCurrentPage,
          changePageSize: globalActions.changePageSize,
          changeQueryString: globalActions.changeQueryString,
          changeSortModel: globalActions.changeSortModel,
          fetchImportStatus: globalActions.fetchImportStatus,
          fetchSearchResultConfig: globalActions.fetchSearchResultConfig,
          search: globalActions.search,
        },
        dispatch,
      ),
    [dispatch],
  );

  const importStatusJs = importStatus?.get('data');

  useEffect(() => {
    fetchSearchResultConfig();
    fetchImportStatus();
  }, [fetchImportStatus, fetchSearchResultConfig]);

  const triggerSearch = async (page, sortModel) => {
    if (!queryString) {
      return;
    }
    const searchOptions = { queryString };

    let sModel;
    if (sortModel) {
      sModel = sortModel;
    } else if (propertyList.get('sortModel')) {
      sModel = propertyList.get('sortModel').toJS();
    }
    const response = await search({
      page,
      pageSize: propertyList.get('pageSize'),
      searchOptions,
      sortField: sModel && sModel.length > 0 ? sModel[0].colId : undefined,
      sortDirection:
        sModel && sModel.length > 0 ? sModel[0].sort.toUpperCase() : undefined,
    });
    if (response?.results && response.results.length === 1) {
      navigate(
        `/taxTransparency/property/${response.results[0].propID}/${response.results[0].ownerID}`,
      );
    }
    changeCurrentPage(page);
  };

  const onChangeSort = (colId) => {
    const sortModel = propertyList.get('sortModel').toJS();
    let newSortModel;
    if (!sortModel || sortModel.length < 1 || sortModel[0].colId !== colId) {
      newSortModel = [
        {
          colId,
          sort: 'asc',
        },
      ];
    }
    if (sortModel && sortModel.length > 0 && sortModel[0].colId === colId) {
      if (sortModel[0].sort === 'asc') {
        newSortModel = [
          {
            colId,
            sort: 'desc',
          },
        ];
      } else {
        newSortModel = [];
      }
    }
    changeSortModel(newSortModel);
    if (propertyList.get('totalCount') <= propertyList.get('pageSize')) {
      return;
    }
    triggerSearch(propertyList.get('currentPage'), newSortModel);
  };

  const renderTable = () => {
    const sortModel = propertyList.get('sortModel').toJS();
    let defaultColumnDefs = searchResultConfig
      .get('data')
      .toJS()
      .map((field) => ({
        headerName: field.displayName,
        field: field.id,
      }));
    defaultColumnDefs = [
      { headerName: 'SEQ', field: 'seq', suppressSorting: true },
    ].concat(defaultColumnDefs);

    if (clientConfig?.getIn(['data', 'useAccountNumber'])) {
      defaultColumnDefs = defaultColumnDefs.filter((c) => c.field !== 'propID');
    } else {
      defaultColumnDefs = defaultColumnDefs.filter(
        (c) => c.field !== 'accountNumber',
      );
    }
    const properties = propertyList.get('data').toJS();
    const rowData = properties.map((property, index) => {
      const value = {};
      searchResultConfig
        .get('data')
        .toJS()
        .forEach((field) => {
          value[field.id] = property[field.id];
        });
      value.seq = index + 1;
      value.ownerID = property.ownerID;
      return value;
    });
    const getSortDirection = (field) => {
      if (sortModel.length > 0 && sortModel[0].colId === field) {
        return sortModel[0].sort === 'asc' ? 'ascending' : 'descending';
      }
      return 'none';
    };

    return (
      <table aria-label='Property search results'>
        <thead>
          <tr>
            {defaultColumnDefs.map((d) => (
              <th
                key={d.field}
                {...(!d.suppressSorting
                  ? {
                      tabIndex: 0,
                      style: { cursor: 'pointer' },
                      'aria-sort': getSortDirection(d.field),
                      onClick: () => onChangeSort(d.field),
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onChangeSort(d.field);
                        }
                      },
                    }
                  : {})}
              >
                <div className='flex-container'>
                  <span>{d.headerName}</span>
                  {Boolean(sortModel) &&
                    sortModel.length > 0 &&
                    sortModel[0].colId === d.field && (
                      <SortIndicator sort={sortModel[0].sort} />
                    )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowData.map((r) => (
            <tr key={`${r.propID}-${r.ownerID}`}>
              {defaultColumnDefs.map((d) => (
                <td key={d.field}>
                  {d.field === 'propID' || d.field === 'accountNumber' ? (
                    <a
                      href={`/taxTransparency/property/${r.propID}/${r.ownerID}`}
                      aria-label={`${d.field === 'propID' ? 'Property ID' : 'Account Number'} ${r[d.field]}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/taxTransparency/property/${r.propID}/${r.ownerID}`,
                        );
                      }}
                    >
                      {r[d.field]}
                    </a>
                  ) : (
                    <span>
                      {d.field === 'situs' || d.field === 'dba'
                        ? removeNullCharacter(r[d.field])
                        : r[d.field]}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <div className='intro-container'>
        <div className='logo-container'>
          <span className='main-title neue-font-medium'>
            <span className='office-title'>{office} Taxes</span>
            <span> | </span>
            <span>Truth-in-Taxation</span>
          </span>
          {clientConfig.getIn(['data', 'showHB3273Link']) ? (
            <span className='tooltip-info-icon-wrap'>
              <RegisterNotificationTooltip>
                <a
                  className='register-link'
                  href={clientConfig.getIn(['data', 'hb3273Url'])}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Register for Notification (opens in new window)'
                >
                  <BellIcon aria-hidden='true'>
                    <g>
                      <path
                        d='M0 12.321a1.026 1.026 0 0 0 1.027 1.027h12.32a1.027 1.027 0 0 0 .767-1.71l-.237-.266a6.169 6.169 0 0 1-1.556-4.094v-.6a5.136 5.136 0 0 0-4.107-5.035v-.616a1.027 1.027 0 1 0-2.053 0v.616a5.136 5.136 0 0 0-4.108 5.031v.6A6.175 6.175 0 0 1 .5 11.371l-.237.266a1.033 1.033 0 0 0-.263.684z'
                        style={{ opacity: 0.4 }}
                      />
                      <path
                        d='M164.107 448a2.053 2.053 0 1 1-4.107 0h4.107z'
                        transform='translate(-154.866 -433.626)'
                      />
                    </g>
                  </BellIcon>
                  <span>Register for Notification</span>
                </a>
              </RegisterNotificationTooltip>
            </span>
          ) : (
            <div />
          )}
        </div>
        <div className='text-container'>
          {clientConfig &&
          clientConfig.getIn(['data', 'showBetaMessage']) === 1 &&
          clientConfig.getIn(['data', 'betaMessage']) ? (
            <div className='disclaimer'>
              {clientConfig.getIn(['data', 'betaMessage'])}
            </div>
          ) : null}
          <div className='main-content-wrapper'>
            <div className='main-content-container'>
              <span>
                Truth-in-Taxation is a concept embodied in the Texas
                Constitution that requires local taxing units to make taxpayers
                aware of tax rate proposals and to afford taxpayers the
                opportunity to roll back or limit tax increases.
              </span>
              <span>
                Creating a budget and adopting a property tax rate to support
                that budget are major functions of a taxing unit's governing
                body. This is accomplished by following Truth-in-Taxation
                requirements to ensure the public is informed of any increases.
                The type of taxing unit determines its applicable{' '}
                <a
                  href='https://comptroller.texas.gov/taxes/property-tax/truth-in-taxation/index.php'
                  style={{ color: '#2e3b8e' }}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Truth-in-Taxation requirements
                  <span className='sr-only'> (opens in new window)</span>.
                </a>
              </span>
              <span>
                This website provides information concerning the property taxes
                that may be imposed on your property by local taxing units, the
                dates and locations of any public hearings on the tax rates of
                the taxing units, and the dates and locations of meetings of the
                governing bodies of the taxing units to vote on the tax rates,
                together with other important property tax information.
              </span>
              <span>
                The 86th Texas Legislature modified the manner in which the
                voter-approval tax rate is calculated to limit the rate of
                growth of property taxes in the state.
              </span>
              <span>
                Please note: The Texas Legislature does not set the tax amount
                of your local taxes. Your property tax burden is decided by your
                locally elected officials (school trustees, city council, county
                commissioners, et al) and all inquiries concerning your taxes
                should be directed to those officials.
              </span>
            </div>
            <div className='left-content-container'>
              <div>
                <img src={mainImage} alt='Truth in Taxation illustration' />
              </div>
            </div>
          </div>
          <div className='main-content-wrapper'>
            {clientConfig &&
            clientConfig.getIn(['data', 'showCustomCADAppealMessage']) === 1 &&
            clientConfig.getIn(['data', 'cadAppealMessage']) ? (
              <AppealMessageContent
                $color={
                  clientConfig.getIn(['data', 'cadAppealMessageColor']) ||
                  '#000000'
                }
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich-text message authored by CAD admins in client config
                dangerouslySetInnerHTML={{
                  __html: clientConfig.getIn(['data', 'cadAppealMessage']),
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className='page property-search'>
        <div className='search-fields-container'>
          <input
            type='text'
            className='search-input'
            placeholder={
              window.innerWidth > 425
                ? 'Search by Name, Address or Property ID (PID#), for example "William Travis" or "1100 Congress Ave" or "151999"'
                : ''
            }
            value={queryString}
            onChange={(e) => changeQueryString(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                triggerSearch(1);
              }
            }}
          />
          <button
            type='button'
            className='search-btn'
            disabled={!queryString}
            onClick={() => triggerSearch(1)}
          >
            SEARCH
          </button>
          <button
            type='button'
            className='clear-btn'
            disabled={!queryString}
            onClick={() => changeQueryString('')}
            aria-label='Clear search'
          >
            CLEAR
          </button>
        </div>
        {importStatusJs === 'In Progress' ? (
          <RefreshIndicator />
        ) : (
          <div className='search-results-container'>
            <div
              className='list-table'
              aria-live='polite'
              aria-busy={propertyList.get('state') === fetchingState.FETCHING}
            >
              {propertyList.get('state') === fetchingState.FETCHING && (
                <div style={{ textAlign: 'center' }} role='status'>
                  <LoadingSpinner
                    style={{ marginTop: 15, display: 'inline-block' }}
                  />
                  <span className='sr-only'>Loading search results</span>
                </div>
              )}
              {propertyList.get('state') === fetchingState.LOADED &&
                propertyList.get('totalCount') > 0 && (
                  <div>{renderTable()}</div>
                )}
              {propertyList.get('state') !== fetchingState.FETCHING &&
                (!propertyList.get('data') ||
                  propertyList.get('totalCount') === 0) && <NoDataHolder />}
            </div>
            {propertyList.get('totalCount') > 0 && (
              <Pagination
                currentPage={propertyList.get('currentPage')}
                pageSize={propertyList.get('pageSize')}
                totalCount={propertyList.get('totalCount')}
                onChangePageSize={changePageSize}
                onPageClick={triggerSearch}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AppealMessageContent = styled.div`
  & > * {
    color: ${(props) => props.$color} !important;
  }

  a {
    color: ${(props) => props.$color} !important;
  }
`;

const BellIcon = styled(SvgIcon)`
  font-size: 24px;
  margin-bottom: -8px;
`;

export default PropertySearch;
